import { useRouter } from 'next/router';
import { Image, Text, Marquee } from '@belong/ui';
import clsx from 'clsx';
import { ViewAllReviews } from 'src/components/view-all-reviews/view-all-reviews';
import css from './social-proof.module.css';

const SOCIAL_PROOF_STATS = [
  {
    count: <RatingStat rating="4.7" />,
    label: 'Google',
  },
  {
    count: <RatingStat rating="4.5" />,
    label: 'Trustpilot',
  },
  {
    count: <RatingStat rating="4.9" />,
    label: 'Thumbtack',
  },
  {
    count: <RatingStat rating="4.8+" />,
    label: 'Apple Store',
  },
  {
    count: <RatingStat rating="A+" showStar={false} />,
    label: 'Better Business Bureau',
  },
];

const SOCIAL_PROOF_LOGOS = [
  {
    src: '/logos/businessinsider_logo_dark_gray.svg',
    alt: 'Business Insider Logo',
    body: 'BI’s Most Promising Consumer\u00a0Startups',
  },
  {
    src: '/logos/techcrunch_logo_dark_gray.svg',
    alt: 'TechCrunch Logo',
    body: '“80M to take the pain out of\u00a0rentals”',
  },
  {
    src: '/logos/sfist_logo_dark_gray.svg',
    alt: 'SFist Logo',
    body: 'Best Property Managers in San\u00a0Francisco',
  },
  {
    src: '/logos/cbinsights_dark_gray.svg',
    alt: 'CB Insights Logo',
    body: 'CB Insights Fintech 250 in the Real Estate\u00a0category',
  },
];

interface SocialProofProps {
  variant?: 'dark' | 'light';
}

export function SocialProof({ variant: _variant = 'light' }: SocialProofProps) {
  const router = useRouter();

  const { cityOrRegionSlug } = router.query;

  const isCityLandingPage = (cityOrRegionSlug as string)?.endsWith(
    'property-management',
  );

  return (
    <div className="xl:max-w-container-xl relative w-full xl:container">
      <div
        className={clsx(
          css.marqueeContainer,
          !isCityLandingPage && css.marqueeFade,
        )}
      >
        <Marquee>
          {SOCIAL_PROOF_STATS.map(({ count, label }) => (
            <div
              key={label}
              style={{ height: 100, width: 240 }}
              className="text-dark-gray flex flex-col items-center justify-center text-center"
            >
              {typeof count === 'string' ? (
                <Text variant="h2" fontWeight="semibold">
                  {count}
                </Text>
              ) : (
                count
              )}
              <Text className="mt-xs text-p1 lg:text-body">{label}</Text>
            </div>
          ))}
          {SOCIAL_PROOF_LOGOS.map(({ src, alt, body }) => (
            <div
              key={src}
              style={{ width: 240, height: 100 }}
              className="text-dark-gray flex flex-col items-center justify-center text-center"
            >
              <div style={{ width: 150, height: 44 }} className="mb-xs">
                <Image src={src} alt={alt} />
              </div>
              <Text variant="p1" className={css.bodyContainer}>
                {body}
              </Text>
            </div>
          ))}
        </Marquee>
      </div>
      <div className="mt-lg">
        <ViewAllReviews />
      </div>
    </div>
  );
}

function RatingStat({
  rating,
  showStar = true,
}: {
  rating: string;
  showStar?: boolean;
}) {
  return (
    <div className="gap-2xs flex items-center justify-center ">
      <Text variant="h2" fontWeight="semibold">
        {rating}
      </Text>
      {showStar && (
        <div style={{ width: '25px' }}>
          <Image src="/icons/yellow_star.svg" alt="Yellow Star" />
        </div>
      )}
    </div>
  );
}
